import React, { useState, useContext, useEffect } from "react";
import { FaShoppingCart, FaMinus, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useCart } from "../../contexts/CartContext"; // Ensure this context provides necessary functions
import axios from "axios";
import { BASE_URL, API_TOKEN, SLINK } from "../../config";

const ProductDisplay = () => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [toastMessage, setToastMessage] = useState(""); // State for toast message
  const [showToast, setShowToast] = useState(false); // State for toast visibility
  const [isAddedToCart, setIsAddedToCart] = useState(false); // State to track if item is added to cart

  const { isRTL } = useLanguage();
  const { isDarkMode } = useContext(ThemeContext);

  const { addToCart, cartProducts, updateCartItemQuantity, currency } =
    useCart(); // Use updateCartItemQuantity from context

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/user/catalogues`, {
          params: { slink: SLINK },
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        const allProducts = response.data.data[0].campaign_entitycards;
        const selectedProduct = allProducts.find(
          (item) => item.product.id === parseInt(id)
        );

        if (selectedProduct) {
          setProduct(selectedProduct.product);
        } else {
          console.error("Product not found");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Initialize quantity based on cartProducts
  const initialQuantity =
    cartProducts.find((item) => item.id === parseInt(id))?.qty || 1;
  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  useEffect(() => {
    // Check if the product is already in the cart when the component mounts
    const cartProduct = cartProducts.find((item) => item.id === id);
    if (cartProduct) {
      setIsAddedToCart(true); // Set to true if the product is already in the cart
    }
  }, [cartProducts, id]);

  const incrementQuantity = () => {
    setQuantity((prev) => {
      const newQuantity = prev + 1;
      const cartProduct = cartProducts.find((item) => item.id === product.id); // Find the cart product
      if (cartProduct) {
        updateCartItemQuantity(cartProduct.cart_product_id, newQuantity); // Update cart with new quantity
      }
      return newQuantity;
    });
  };

  const decrementQuantity = () => {
    setQuantity((prev) => {
      const newQuantity = prev > 1 ? prev - 1 : 1;
      const cartProduct = cartProducts.find((item) => item.id === product.id); // Find the cart product
      if (cartProduct) {
        updateCartItemQuantity(cartProduct.cart_product_id, newQuantity); // Update cart with new quantity
      }
      return newQuantity;
    });
  };

  const handleAddToCart = () => {
    const cartProduct = cartProducts.find((item) => item.id === product.id);
    if (!cartProduct) {
      console.log("Adding to cart:", product.id);
      addToCart(product.id); // Use addToCart from context
      updateCartItemQuantity(product.id, quantity); // Update cart with quantity
      setToastMessage("Item added to cart successfully!");
      setIsAddedToCart(true); // Set the state to indicate item is added
    } else {
      setToastMessage("Item already in cart!");
      setIsAddedToCart(true); // Ensure the state is true if already in cart
    }
    setShowToast(true); // Show the toast
  };

  const handleGoToCart = () => {
    navigate("/cart"); // Navigate to the cart items page
  };

  // Function to hide the toast after a few seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000); // Hide after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  if (isLoading) {
    return (
      <div
        className={`flex justify-center items-center h-screen ${
          isDarkMode ? "bg-black text-white" : "bg-white text-black"
        }`}
      >
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!product) {
    return <div className="text-center mt-8">Product not found</div>;
  }

  // Check if the product is already in the cart
  const cartProduct = cartProducts.find((item) => item.id === product.id);
  const isProductInCart = !!cartProduct;

  return (
    <div
      className={`container mx-auto px-4 py-8 ${
        isDarkMode ? "bg-black text-white" : "bg-white text-black"
      }`}
    >
      {showToast && (
        <div
          className={`fixed top-5 right-5 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg`}
        >
          {toastMessage}
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:space-x-8">
        {/* Product Image */}
        <div className="lg:w-2/5 mb-4 lg:mb-0">
          <img
            src={product.photo[0].url}
            alt={product.name}
            className="w-full h-64 md:h-80 lg:h-96 object-contain rounded-lg"
          />
        </div>

        {/* Product Details */}
        <div className="lg:w-3/5">
          <h1 className="text-2xl font-bold mb-4">{product.name}</h1>
          <p className="text-xl mb-4">
            <span dangerouslySetInnerHTML={{ __html: currency }} />
            {product.price ? product.price : "0.00"}{" "}
            {/* Show 0.00 if price is not available */}
          </p>
          <p className="mb-6">{product.description}</p>

          <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2.5 mb-6">
            {/* Quantity Controls */}
            <div
              className={`flex justify-center lg:justify-start items-center rounded-lg overflow-hidden ${
                isDarkMode ? "bg-[#2b2924]" : ""
              } p-1 mb-4 lg:mb-0`}
            >
              <button
                onClick={decrementQuantity}
                className={`bg-[#f8d25c] text-black px-2 py-2 rounded-l-md rounded-lg ${
                  isDarkMode ? "bg-[#f8d25c]" : ""
                }`}
              >
                <FaMinus />
              </button>
              <span
                className={`px-4 py-2 font-bold mx-2 ${
                  isDarkMode ? "text-white" : "text-black"
                }`}
              >
                {quantity}
              </span>
              <button
                onClick={incrementQuantity}
                className={`bg-[#f8d25c] text-black px-2 py-2 rounded-r-md rounded-lg ${
                  isDarkMode ? "bg-[#f8d25c]" : ""
                }`}
              >
                <FaPlus />
              </button>
            </div>

            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2.5">
              {/* Add to Cart / Go to Cart Button */}
              <button
                onClick={isProductInCart ? handleGoToCart : handleAddToCart}
                className="bg-[#f8d25c] text-black px-9 py-3 flex items-center justify-center rounded-lg w-full md:w-auto"
              >
                <FaShoppingCart className={`${isRTL ? "ml-2" : "mr-2"}`} />
                {isProductInCart
                  ? isRTL
                    ? "اذهب إلى السلة"
                    : "Go to Cart"
                  : isRTL
                  ? "أضف إلى السلة"
                  : "Add to Cart"}
              </button>

              {/* Continue Shopping Button */}
              <button
                onClick={() => navigate(-1)}
                className={`bg-[#2b2924] text-white px-9 py-3 rounded-lg w-full md:w-auto ${
                  isDarkMode ? "bg-[#2b2924]" : ""
                }`}
              >
                {isRTL ? "متابعة التسوق" : "Continue Shopping"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDisplay;
